import { computed, ref, shallowRef, watch, type Ref } from 'vue';
import type { BaseModel } from '@/_api/models/base';
import type { BaseService } from '@/_api/services/base';
import { db, toStorableData, type EntityTableName, type StoredRow } from '@/lib/db';

interface SyncState {
  running: boolean;
  progress: number | null;
  date: Date | null;
  error: boolean;
}

interface SyncDataOptions<T extends BaseModel> {
  /** The service to use for fetching data */
  service: BaseService<T>;

  /**
   * Dexie table name for this entity — corresponds to a key on `OvatuDB`.
   * Must be declared in `app/lib/db.ts`.
   */
  key: EntityTableName;

  /** Unique identifier for the current account (e.g., GUID) */
  accountId: Ref<string | null> | string;

  /** Required feature flag for this sync */
  feature?: string;

  /** Required permission grants - e.g., { employee: ['view'] } */
  grants?: Record<string, string[]>;
}

interface SyncDataReturn<T extends BaseModel> {
  /** Reactive items array */
  items: Ref<T[]>;

  /** Number of items */
  count: Ref<number>;

  /** Whether initial data has been loaded from storage */
  hydrated: Ref<boolean>;

  /** Sync state (running, progress, date, error) */
  syncState: Ref<SyncState>;

  /** Whether sync is currently running */
  isSyncing: Ref<boolean>;

  /** Sync progress (0-1) */
  syncProgress: Ref<number | null>;

  /** Sync data (force = true to bypass cache) */
  sync: (options?: { force?: boolean; change?: boolean }) => Promise<void>;

  /** Find item by ID using objectID() */
  findById: (id: string | number) => T | undefined;
}

const SYNC_TTL_MS = 5 * 60 * 1000;

/**
 * Backs a Pinia store with a server-synced list of entity rows, persisted to
 * Dexie (IndexedDB). See `app/lib/db.ts` for schema. See .docs/performance.md
 * for the migration rationale from the previous one-blob-per-entity localforage
 * layout.
 *
 * Contract preserved from the previous implementation:
 *   • Consumers read `items.value` as an in-memory array of Model instances.
 *   • `sync()` hydrates from storage on first call, then fetches deltas from
 *     the server, merges them, and writes back per-row.
 *   • Errors are swallowed internally (see try/catch in the sync action) —
 *     callers can assume the promise always resolves.
 */
export const useSyncData = <T extends BaseModel>(
  options: SyncDataOptions<T>
): SyncDataReturn<T> => {
  const { service, key, feature, grants } = options;

  const items: Ref<T[]> = ref([]);
  const hydrated = ref(false);

  // Secondary index: Map<objectID, item> maintained alongside `items` for
  // O(1) `findById` lookups. Previously this was an O(n) scan on every call
  // — called from ~30 components (schedule grid, message chat, customer
  // profile, etc.), sometimes many times per render. shallowRef so assigning
  // a new Map triggers reactivity without Vue trying to deep-observe the
  // Map's entries.
  const byId = shallowRef(new Map<string, T>());

  const rebuildIndex = (list: T[]) => {
    const next = new Map<string, T>();
    for (const item of list) {
      const id = item.objectID();
      if (id) next.set(id, item);
    }
    byId.value = next;
  };

  const syncState = ref<SyncState>({
    running: false,
    progress: null,
    date: null,
    error: false,
  });

  const isSyncing = computed(() => syncState.value.running);
  const syncProgress = computed(() => syncState.value.progress);
  const count = computed(() => items.value.length);

  const getAccountId = (): string | null => {
    if (typeof options.accountId === 'string') return options.accountId;
    return options.accountId.value;
  };

  const syncStateKey = (accountId: string) => `${accountId}.${key}`;

  let inFlight: { accountId: string; promise: Promise<void>; controller: AbortController; refresh?: Promise<void> } | null = null;

  watch(getAccountId, () => {
    inFlight?.controller.abort();
    items.value = [];
    rebuildIndex([]);
    hydrated.value = false;
    syncState.value = { running: false, progress: null, date: null, error: false };
  }, { flush: 'sync' });

  const performSync = async (
    accountId: string,
    signal: AbortSignal,
    { force = false, change = false }: { force?: boolean; change?: boolean } = {},
  ): Promise<void> => {
    let lastSyncDate = syncState.value.date;

    // Hydrate from Dexie if not yet done (or if account switched — `change`).
    if (!hydrated.value || change) {
      const Model = service.model;

      let storedItems: T[] = [];
      let storedSync;
      try {
        const storedRows = await db.table<StoredRow>(key)
          .where('accountId').equals(accountId).toArray();
        storedSync = await db.syncStates.get(syncStateKey(accountId));
        storedItems = storedRows.map(row => new Model(row.data)) as T[];
      } catch (error) {
        // Local storage is optional. A failed read must still allow a full API sync.
        console.error(`[useSyncData:${key}] Failed to read cache`, error);
      }

      if (signal.aborted || getAccountId() !== accountId) return;

      items.value = storedItems;
      rebuildIndex(items.value);
      lastSyncDate = storedItems.length ? storedSync?.date ?? null : null;
      syncState.value = {
        running: false, progress: null, date: lastSyncDate, error: storedSync?.error ?? false,
      };

      hydrated.value = true;
    }

    // `change` means "hydrate the newly-active account", not "force a network
    // request". A fresh cache remains valid after an account switch.
    if (!force && lastSyncDate) {
      const age = Date.now() - new Date(lastSyncDate).getTime();
      if (age >= 0 && age < SYNC_TTL_MS) {
        return;
      }
    }

    const date = new Date();

    syncState.value.running = true;
    syncState.value.progress = 0;

    const sinceDate = force ? undefined : (lastSyncDate ?? undefined);

    // Build lookup of existing items (by objectID) + track which rows we
    // touch so we can write them back to Dexie per-row instead of
    // re-serialising the whole collection.
    const existing: Record<string, T> = {};
    const touchedIds = new Set<string>();
    const deletedIds = new Set<string>();

    if (!force) {
      for (const item of items.value) {
        const id = item.objectID();
        if (id) existing[id] = item;
      }
    }

    const errors: Error[] = [];

    try {
      await service.listAll(
        sinceDate,
        (page, pageCount, list) => {
          const pageItems = Array.isArray(list.items) ? list.items : [];

          for (const item of pageItems) {
            const id = item.objectID();
            if (id) {
              existing[id] = item;
              touchedIds.add(id);
            }
          }

          if (list.deleted) {
            for (const id of list.deleted) {
              const key = String(id);
              delete existing[key];
              deletedIds.add(key);
              touchedIds.delete(key);
            }
          }

          if (getAccountId() === accountId) {
            syncState.value.progress = pageCount > 0 ? page / pageCount : 0;
          }
        },
        3,
        { signal, accountId },
      );
    } catch (e: any) {
      if (e?.status === 304) {
        // Data is up to date
      } else {
        errors.push(e);
      }
    }

    // Never publish or persist a sync that crossed an account boundary.
    if (signal.aborted || getAccountId() !== accountId) return;

    const nextItems = Object.values(existing);
    const nextError = errors.length > 0;
    const nextDate = nextError ? null : date;

    if (getAccountId() === accountId) {
      items.value = nextItems;
      rebuildIndex(items.value);
      syncState.value = { running: false, progress: null, date: nextDate, error: nextError };
    }

    // Persist only the rows we touched, not the whole collection — this is
    // the main write-path win over the previous localforage-blob approach.
    // All three Dexie operations are bundled in one RW transaction so a
    // partial failure can't leave the store half-updated.
    try {
      const writeRows: StoredRow[] = [];

      for (const id of touchedIds) {
        const item = existing[id];
        if (!item) continue;
        writeRows.push({
          accountId,
          id,
          // Model instances aren't safe to put into IDB directly — we
          // persist the JSON-serialisable shape only. See toStorableData.
          data: toStorableData(item),
        });
      }

      const deletePKs = Array.from(deletedIds).map(
        (id) => [accountId, id] as [string, string]
      );

      await db.transaction('rw', db.table(key), db.syncStates, async () => {
        // On a successful force sync the server returned the current full
        // set, so wipe any account-scoped rows first. Without this, any
        // row the server no longer returns (hard-deleted elsewhere,
        // permission-filtered, archived…) would linger in Dexie forever
        // — the incremental delete path only fires for IDs the server
        // explicitly reports in `response.deleted`, which a non-delta
        // (force) response doesn't produce. Matches the clear-and-rebulk
        // pattern useServiceStore uses for its non-paginated endpoint.
        //
        // Gated on `!errors.length` so a half-failed force sync doesn't
        // wipe the local cache and leave only partial new data behind.
        if (force && !errors.length) {
          await db.table<StoredRow>(key).where('accountId').equals(accountId).delete();
        }
        if (writeRows.length) {
          await db.table<StoredRow>(key).bulkPut(writeRows);
        }
        if (deletePKs.length) {
          await db.table<StoredRow>(key).bulkDelete(deletePKs);
        }
        await db.syncStates.put({
          key: syncStateKey(accountId),
          accountId,
          entity: key,
          date: nextDate,
          error: nextError,
        });
      });
    } catch (writeError) {
      // Persistence failures shouldn't surface to callers — next sync can
      // retry. But do flag them: silent data-loss is worse than noise.
      console.error(`[useSyncData:${key}] Failed to persist to Dexie`, writeError);
    }
  };

  const sync = (options: { force?: boolean; change?: boolean } = {}): Promise<void> => {
    const accountId = getAccountId();
    if (!accountId) return Promise.resolve();

    if (inFlight) {
      if (inFlight.accountId === accountId && !inFlight.controller.signal.aborted) {
        if (options.force) {
          // An update during a request needs a fetch after that request completes.
          inFlight.refresh ??= inFlight.promise.then(() => {
            if (getAccountId() === accountId) return sync(options);
          });
          return inFlight.refresh;
        }
        return inFlight.promise;
      }

      // Finish persisting the previous account before starting the new one.
      // The performSync guards above prevent stale rows being published while
      // the active account changes.
      return inFlight.promise.then(() => sync(options));
    }

    const controller = new AbortController();
    const pending = performSync(accountId, controller.signal, options);
    const tracked = pending.finally(() => {
      if (inFlight?.promise === tracked) {
        inFlight = null;
      }
    });

    inFlight = { accountId, promise: tracked, controller };
    return tracked;
  };

  const findById = (id: string | number): T | undefined => {
    return byId.value.get(id.toString());
  };

  return {
    items,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    sync,
    findById,
  };
};
